<script lang="ts" setup>
import { useCategoryStore } from '~/store/categoryStore'
import type { CategoryTree } from '~/types/searchTypes'
import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import { CategoryIdEnum, PjCategoryIdEnum, SortDirection } from '~/types/searchTypes'
import QueryEnum from '~/enums/QueryEnum'
import getBrands from '~/composables/constants/brands'
import { useStoreConfig } from '~/store/storeConfig'
import getCategoryIcon from '~/utils/categories/getCategoryIcon'

enum BannerOptions {
  First = '1 Big',
  Second = '1 Big, 3 Small',
}
type BannerTypes = BannerOptions.First | BannerOptions.Second

const props = defineProps<{
  variant: BannerTypes | BannerTypes[]
  bigBanner: {
    image: TranslatableString
    slug: TranslatableString
  }
  smallBanners?: {
    image: TranslatableString
    slug: TranslatableString
  }[]
}>()

const { t } = useLang()
const categories = useCategoryStore()
const hoveredCategory = ref<CategoryTree>()
const storeConfig = useStoreConfig()
const { isPerkuJavTheme } = useTheme()
const brands = getBrands(isPerkuJavTheme ? PjCategoryIdEnum : CategoryIdEnum)

const mobileBanners = [
  {
    slug: props.bigBanner.slug,
    mobile: props.bigBanner.image,
    desktop: null,
    openLinksInNewTab: false,
  },
  ...(props.smallBanners?.map((banner) => ({
    slug: banner.slug,
    mobile: banner.image,
    desktop: null,
    openLinksInNewTab: false,
  })) ?? []),
]
const variant = typeof props.variant === 'string' ? props.variant : props.variant?.[0]

const onLeave = () => {
  hoveredCategory.value = undefined
}

const offerLabels = computed(() => ({
  [SortDirection.PriceLowToHigh]: t('search.cheapest'),
  [SortDirection.BestValue]: t('search.best-value'),
  [SortDirection.BestSellers]: t('search.most-popular'),
  [SortDirection.NewArrivals]: t('search.new-arrivals'),
}))
</script>

<template>
  <div class="megamenu" @mouseleave="onLeave">
    <template v-if="!storeConfig.getIsMobile">
      <div class="main-header">
        <Section class="flex">
          <div class="header-categories flex py-2 px-6 items-center">
            <NuxtLink
              :to="buildCategoryUrl(null)"
              class="header-category text-lg flex px-4"
              :class="{
                current: hoveredCategory?.id === 0,
              }"
              @mouseenter="hoveredCategory = { ...getEmptyCategory(t), children: categories.getCategoryTree }">
              <span>
                {{ t('common.all-products') }}
              </span>
            </NuxtLink>
            <hr class="vertical-divider h-2/3 w-0" />
            <template v-for="(category, index) in categories.getCategoryTree.slice(0, 5)" :key="category.id">
              <NuxtLink
                :to="buildCategoryUrl(category.slug)"
                class="header-category flex text-lg px-4"
                :class="{
                  current: category.id === hoveredCategory?.id,
                }"
                @mouseenter="hoveredCategory = category">
                <span>{{ category.name }}</span>
              </NuxtLink>
              <hr v-if="index + 1 !== categories.getCategoryTree.length" class="vertical-divider h-2/3 w-0" />
            </template>
          </div>
        </Section>
      </div>
      <Section>
        <div class="relative flex">
          <!-- Removed by request. -->
          <div v-if="false" class="categories flex flex-col mt-4 z-10 w-full pr-6">
            <template v-for="category in categories.getCategoryTree.slice(0, 10)" :key="category.id">
              <NuxtLink v-if="!category.children.length" class="category" @mouseenter="onLeave">
                <div class="w-8">
                  <img :src="getCategoryIcon(category.id)" alt="" />
                </div>
                {{ category.name }}
              </NuxtLink>
              <div v-else class="category" @mouseenter="hoveredCategory = category">
                <div class="w-8">
                  <img :src="getCategoryIcon(category.id)" alt="" />
                </div>
                {{ category.name }}
              </div>
            </template>
          </div>
          <div
            v-if="hoveredCategory?.id != null"
            class="hovered absolute top-0 w-full h-full right-0 z-[11]"
            @mouseleave="onLeave">
            <div
              class="bg-white dark:bg-dmGray1 border-gray2 dark:border-dmGray4 border w-full mt-4 bottom-0 rounded-sm shadow-md dark:shadow-dmGray4">
              <div class="mega-menu-content flex">
                <section class="flex flex-col">
                  <div class="section-title">
                    {{ t('category.categories') }}
                  </div>
                  <NuxtLink
                    v-for="cat in hoveredCategory?.children.slice(0, 8)"
                    :key="cat.id"
                    :to="buildCategoryUrl(cat.slug)">
                    {{ cat.name }}
                  </NuxtLink>
                  <NuxtLink :to="buildCategoryUrl(hoveredCategory?.slug)" class="all-categories-link">
                    {{ t('common.all-products') }}
                  </NuxtLink>
                </section>
                <hr />
                <section class="flex flex-col">
                  <div class="section-title">{{ t('category.offers') }}</div>
                  <NuxtLink
                    v-for="(offer, index) in offerLabels"
                    :key="index"
                    :to="
                      buildCategoryUrl(hoveredCategory?.slug, false, {
                        s: index,
                      })
                    ">
                    {{ offer }}
                  </NuxtLink>
                </section>
                <div v-if="hoveredCategory?.id && brands[hoveredCategory.id]?.length">
                  <div class="section-title">
                    {{ t('category.most-popular-brands') }}
                  </div>

                  <div class="grid gap-4 grid-cols-2 xl:grid-cols-3">
                    <div
                      v-for="(item, index) in brands[hoveredCategory.id as CategoryIdEnum]"
                      :key="`${item.name}-${index}`"
                      class="flex">
                      <NuxtLink
                        class="w-full"
                        :to="
                          buildCategoryUrl(
                            hoveredCategory.slug,
                            false,
                            {
                              [QueryEnum.Manufacturer]: item.name,
                            },
                            false
                          )
                        ">
                        <div class="brand">
                          <img :src="item.img" :alt="item.name" />
                        </div>
                      </NuxtLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="banners mt-4 w-full h-full flex flex-col">
            <div class="main-banner w-full h-full flex items-center justify-center overflow-hidden rounded-sm flex-1">
              <NuxtLink
                :to="getWidgetTextTranslation(props.bigBanner.slug) || '/'"
                class="big-banner"
                :style="{ backgroundImage: `url('${getWidgetTranslatedMediaUrl(props.bigBanner.image)}')` }" />
            </div>
            <div v-if="variant === BannerOptions.Second && smallBanners" class="flex mini-banners h-[150px]">
              <div v-for="({ image, slug }, index) in smallBanners" :key="index">
                <NuxtLink
                  :to="getWidgetTextTranslation(slug) || '/'"
                  class="small-banner"
                  :style="{ backgroundImage: `url('${getWidgetTranslatedMediaUrl(image)}')` }" />
              </div>
            </div>
          </div>
        </div>
      </Section>
    </template>
    <template v-else>
      <BannerSlider :banners="mobileBanners" />
    </template>
  </div>
</template>

<style lang="postcss" scoped>
.main-header {
  border-bottom: theme('vars.borderLight');

  .vertical-divider {
    margin: 0 0.5rem;
  }

  .all-products {
    padding: 1rem;
    font-weight: 600;
    min-width: 359px;
    background: theme('colors.lightPurple');
    transition: all 0.1s ease-in;
    border-bottom: 2px solid theme('colors.purple');
    align-items: center;

    &:hover {
      background: theme('colors.purple');
      color: theme('colors.white');

      img {
        filter: invert(1);
      }
    }

    img {
      width: 18px;
      height: 18px;
    }
  }

  .header-categories {
    width: 665px;
    flex: 1;
    font-weight: 500;

    .header-category {
      max-width: min-content;
      width: 100%;
      white-space: nowrap;

      > span {
        text-wrap: nowrap;
        overflow: hidden;
        width: 100%;
        text-overflow: ellipsis;
        max-width: 100%;
        line-height: 2;
      }

      &.current {
        position: relative;

        &:after {
          content: '';
          display: block;
          position: absolute;
          left: 50%;
          margin-left: -12px;
          height: 16px;
          width: 16px;
          background: white;
          rotate: 45deg;
          border-top: 1px solid theme('colors.gray2');
          border-left: 1px solid theme('colors.gray2');
          top: 53px;
          z-index: 10;
        }
      }

      &:hover {
        border-radius: theme('borderRadius.md');
        background-color: theme('colors.neutral7');
        text-decoration: none;
      }
    }
  }
}

.dark {
  .main-header {
    border-bottom: 1px solid theme('colors.dmGray4');

    .header-categories {
      .header-category {
        &:hover {
          background-color: theme('colors.dmGray1');
        }

        &.current {
          &:after {
            background: theme('colors.dmGray4');
            border-top: 1px solid theme('colors.dmGray4');
            border-left: 1px solid theme('colors.dmGray4');
          }
        }
      }
    }
  }
}

.hovered {
  > div {
    height: calc(100% - 1rem);
  }

  .mega-menu-content {
    padding: 1.25rem;
    height: 100%;
    font-weight: 500;
    font-size: 14px;
    width: 100%;
    z-index: 10;

    .section-title {
      @apply px-2 py-[0.5625rem] text-neutral3 dark:text-dmText3 uppercase text-base font-bold;
    }

    > section {
      max-width: 360px;
      width: 100%;

      a {
        font-weight: 500;
        text-transform: none;

        &.all-categories-link {
          color: theme('colors.brand.primary') !important;
          font-weight: 700;
        }
      }

      > * {
        padding: 0.75rem 0.5rem;
      }
    }
  }

  .column {
    padding: 0.5rem !important;
  }

  .brand {
    @apply bg-gray1 dark:bg-dmGray1;

    position: relative;
    padding: 1rem 1.375rem;
    border-radius: theme('vars.borderRadiusLight');
    height: 5rem;
    display: flex;

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }

    &:hover {
      transition: all 0.1s linear;
      cursor: pointer;
      background-color: theme('colors.gray2');
    }
  }
}

.categories {
  min-width: 359px;
  max-width: 359px;

  > a {
    @apply !bg-neutral8 dark:!bg-dmGray2;
    cursor: pointer;

    &:hover {
      border-right: theme('vars.borderLight') !important;
    }
  }

  .category {
    display: flex;
    align-items: center;
    padding: 1rem;
    border: theme('vars.borderLight');
    border-bottom: none;
    background-color: theme('colors.white');
    font-weight: 500;

    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
    width: 100%;
    overflow: hidden;

    &:last-child {
      border-bottom: theme('vars.borderLight');
    }

    &:hover {
      border-right: none;
    }

    img {
      width: 18px;
      height: 18px;
    }
  }

  &:before {
    background-color: theme('colors.white');
    border: theme('vars.borderLight');
    content: '';
    height: 16px;
    left: 18px;
    position: absolute;
    top: 8px;
    transform: rotate(45deg);
    width: 16px;
    z-index: -1;
  }
}

.banners {
  max-height: 683px;
  height: auto;

  .main-banner {
    border: theme('vars.borderLight');
    aspect-ratio: 3.3/1;
  }

  .mini-banners {
    width: 100%;
    padding-top: 8px;

    > div {
      flex: 1;
      border-radius: theme('borderRadius.sm');
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      border: theme('vars.borderLight');

      &:not(:last-child) {
        margin-right: 8px;
      }
    }
  }

  .small-banner,
  .big-banner {
    height: 100%;
    width: 100%;
    background-position: left;
    background-size: cover;
  }
}
</style>
